import {
  railSelectionKey,
  useRailSelectionOptional,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { buildSkillsRailDragPayload } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/buildSkillsRailDragPayload";
import { SkillsRailSkillRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/SkillsRailSkillRow";
import type { Skill } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";

/** Draggable skill row — must render inside `TransferDndProvider`. */
export function SkillsRailDraggableSkillRow({
  skill,
  dragNamespace,
  active,
  section = "canonical",
}: {
  skill: Skill;
  dragNamespace: string;
  active?: boolean;
  section?: RailSelectionSource;
}) {
  const selection = useRailSelectionOptional();
  const rowKey = railSelectionKey(section, "skill", skill.id);

  const draggableData = useMemo(() => {
    const selectedResources = selection?.getSelectedResources() ?? [];
    const selectedSkills = selectedResources.filter((r) => r.kind === "skill");
    const inMulti =
      !!selection?.isSelected(rowKey) && selectedSkills.length > 1;
    const allSkills =
      inMulti && selectedResources.every((r) => r.kind === "skill");
    const skills = allSkills ? selectedSkills.map((r) => r.data) : [skill];
    if (skills.length === 1) {
      return buildSkillsRailDragPayload(skills[0]!);
    }
    const entities = skills.map((s) => ({
      type: "skill" as const,
      platform: "cohesive" as const,
      data: {
        id: s.id,
        name: s.name,
        description: s.description,
        type: s.type,
        status: s.status,
        icon: s.icon,
      },
    })) as Entity[];
    return {
      kind: "skill" as const,
      skillIds: skills.map((s) => s.id),
      primaryName: `${skills.length} skills`,
      entities,
    };
  }, [skill, selection, rowKey]);

  const draggable = useDraggable({
    id: `skills-rail:${dragNamespace}:${skill.id}`,
    data: draggableData,
  });

  return (
    <SkillsRailSkillRow
      skill={skill}
      active={active}
      section={section}
      dragRef={draggable.setNodeRef}
      dragProps={{
        ...draggable.attributes,
        ...draggable.listeners,
      }}
      isDragging={draggable.isDragging}
    />
  );
}
